/**
 * A lead, opened: header (name, tap-to-call, email, fit, estimate, stage picker), then the lead's story in places that never mix:
 * Where things stand (their last message, our last email and who let it go, what's next), Waiting on you (every draft and task),
 * quick actions, Planned, not sent yet (follow-ups with their due date and whether they go by themselves, booked calls), What has
 * actually happened (their words, what was sent, calls), Rogers' notes (internal, folded) and a Details fold (contact, source, UTM,
 * consent record, Rogers personalization fields).
 * Data: GET /api/workspaces/:wsId/leads/:leadId/timeline (server/lead-desk-routes.ts); every action uses an existing route.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle, Ban, Bot, Brain, Calendar, CalendarPlus, Check, ChevronDown, ChevronUp, Clock, Copy, Flame, Inbox, Loader2, Mail,
  MessageSquare, MoreHorizontal, Pause, Pencil, Phone, PhoneCall, Play, Reply, Send, Sparkles, StickyNote, Trash2, UserCheck, UserPlus, UserX, X,
} from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import BookDialog from "./BookDialog";
import {
  FIT_LABEL, FIT_STYLE, STAGE_LABEL, STAGE_ORDER, STAGE_STYLE, TASK_KIND_LABEL, dateTime, getJson, leadsKey, relTime, sendJson, telHref, timelineKey,
  type OpenTask, type PendingEmail, type Stage, type TimelineItem, type TimelineResponse,
} from "./api";

export default function LeadPanel({ workspaceId, leadId, onClose, canDelete }: { workspaceId: number; leadId: number | null; onClose: () => void; canDelete: boolean }) {
  return (
    <Sheet open={leadId != null} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-y-auto" data-testid="lead-panel">
        {leadId != null && <LeadPanelBody key={leadId} workspaceId={workspaceId} leadId={leadId} onClose={onClose} canDelete={canDelete} />}
      </SheetContent>
    </Sheet>
  );
}

type Composer = null | "ask" | "reply";

function LeadPanelBody({ workspaceId, leadId, onClose, canDelete }: { workspaceId: number; leadId: number; onClose: () => void; canDelete: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [bookOpen, setBookOpen] = useState(false);
  const [composer, setComposer] = useState<Composer>(null);
  const [composerText, setComposerText] = useState("");
  const [confirm, setConfirm] = useState<null | "block" | "delete">(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery<TimelineResponse>({
    queryKey: timelineKey(workspaceId, leadId),
    queryFn: () => getJson(`/api/workspaces/${workspaceId}/leads/${leadId}/timeline`),
    refetchInterval: 30_000,
    staleTime: 5_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: leadsKey(workspaceId) });
  const fail = (title: string) => (e: any) => toast({ title, description: e?.message, variant: "destructive" });

  const stage = useMutation({
    mutationFn: (s: Stage | "blocked") => sendJson("POST", `/api/workspaces/${workspaceId}/leads/${leadId}/stage`, { stage: s }),
    onSuccess: (_r, s) => { refresh(); toast({ title: s === "blocked" ? "Lead blocked" : `Moved to ${STAGE_LABEL[s as Stage]}` }); },
    onError: fail("Couldn't change the stage"),
  });
  const pause = useMutation({
    mutationFn: (paused: boolean) => sendJson("PATCH", `/api/leads/${leadId}/nurture-pause`, { paused }),
    onSuccess: (_r, paused) => { refresh(); toast({ title: paused ? "Follow-ups paused" : "Follow-ups resumed" }); },
    onError: fail("Couldn't change follow-ups"),
  });
  const ask = useMutation({
    mutationFn: (message: string) => sendJson("POST", `/api/leads/${leadId}/nurture`, { message }),
    onSuccess: () => { refresh(); setComposer(null); setComposerText(""); toast({ title: "Rogers wrote a draft", description: "It's waiting for your approval in Next step." }); },
    onError: fail("Rogers couldn't write that"),
  });
  const logReply = useMutation({
    mutationFn: (content: string) => sendJson("POST", `/api/workspaces/${workspaceId}/leads/${leadId}/reply`, { content, channel: "email" }),
    onSuccess: (r: any) => {
      refresh(); setComposer(null); setComposerText("");
      toast({ title: "Reply added", description: r?.aiSkipped ? r.message : r?.outboxItemId ? "Rogers drafted an answer for your approval." : undefined });
    },
    onError: fail("Couldn't add the reply"),
  });
  const realLead = useMutation({
    mutationFn: () => sendJson("POST", `/api/workspaces/${workspaceId}/leads/${leadId}/real-lead`, {}),
    onSuccess: (r: any) => {
      refresh();
      toast({ title: "Marked as a real lead", description: r?.firstReply?.queuedId ? "Rogers wrote the first reply; it follows your email setting." : r?.firstReply?.skipped ? `No first reply: ${r.firstReply.skipped}` : "Rogers will follow up." });
    },
    onError: fail("Couldn't mark it as a real lead"),
  });
  const del = useMutation({
    mutationFn: () => sendJson("DELETE", `/api/leads/${leadId}`),
    onSuccess: () => { refresh(); toast({ title: "Lead deleted" }); onClose(); },
    onError: fail("Couldn't delete the lead"),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <SheetHeader className="sr-only"><SheetTitle>Loading the lead</SheetTitle><SheetDescription>Lead details</SheetDescription></SheetHeader>
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6 space-y-3" data-testid="lead-panel-error">
        <SheetHeader><SheetTitle>Lead</SheetTitle><SheetDescription className="sr-only">Lead details</SheetDescription></SheetHeader>
        <p className="text-sm text-muted-foreground">{(error as any)?.status === 404 ? "This lead isn't in this workspace (it may have been deleted)." : (error as Error)?.message || "Couldn't load this lead."}</p>
      </div>
    );
  }

  const { lead, pendingEmails, openTasks, items } = data;
  const paused = !!lead.nurturePaused;
  const terminal = lead.stage === "client" || lead.stage === "lost";
  const busy = stage.isPending || pause.isPending;

  return (
    <div className="flex flex-col" data-testid={`lead-panel-${lead.id}`}>
      {/* header */}
      <div className="border-b border-border p-5 pr-12 space-y-3">
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle className="text-xl font-display" data-testid="lead-panel-name">{lead.name}</SheetTitle>
          <SheetDescription className="text-xs">From {lead.source || "unknown source"} · added {relTime(isoOrNull(lead.createdAt))}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {lead.phone ? (
            <a href={telHref(lead.phone)} className="inline-flex items-center gap-1.5 text-blue-400 hover:underline" data-testid="link-lead-phone"><Phone size={14} />{lead.phone}</a>
          ) : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Phone size={14} />No phone</span>}
          {lead.email ? (
            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 text-foreground/80 hover:underline min-w-0 break-all" data-testid="link-lead-email"><Mail size={14} className="shrink-0" />{lead.email}</a>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={lead.stage} onValueChange={(v) => stage.mutate(v as Stage)} disabled={busy}>
            <SelectTrigger className={cn("h-8 w-auto gap-2 border text-xs font-medium", STAGE_STYLE[lead.stage])} data-testid="select-lead-stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_ORDER.map((s) => <SelectItem key={s} value={s} data-testid={`option-stage-${s}`}>{STAGE_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          {lead.fit && <span className={cn("rounded-md border border-border px-2 py-1 text-xs", FIT_STYLE[lead.fit])} title={lead.fitReason || undefined} data-testid="lead-fit">Fit: {FIT_LABEL[lead.fit]}</span>}
          {lead.estimateText && <span className="rounded-md border border-border px-2 py-1 text-xs text-foreground/80" data-testid="lead-estimate">{lead.estimateText}</span>}
          {lead.optedOut && <span className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-xs text-red-400" data-testid="lead-opted-out"><Ban size={12} />Opted out</span>}
          {paused && !terminal && <span className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground" data-testid="lead-paused">Follow-ups paused</span>}
        </div>
        {lead.need && <p className="text-sm text-foreground/80"><span className="text-muted-foreground">Needs: </span>{lead.need}</p>}
        {(() => {
          const screen = (lead.customFields as any)?._screen;
          if (!screen || screen.verdict !== "not_prospect" || screen.overriddenBy) return null;
          const label = screen.kind === "job_seeker" ? "a job enquiry" : screen.kind === "spam" ? "spam" : screen.kind === "partnership_pitch" ? "a partnership pitch" : "someone selling to you";
          return (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm space-y-2" data-testid="lead-set-aside">
              <p><span className="font-medium">Set aside as {label}.</span> <span className="text-foreground/80">{screen.reason}</span></p>
              <p className="text-xs text-muted-foreground">Rogers did not email, follow up or offer a booking.</p>
              <Button size="sm" variant="outline" onClick={() => realLead.mutate()} disabled={realLead.isPending} data-testid="button-real-lead">
                {realLead.isPending ? <Loader2 className="animate-spin" /> : <Check />} It's a real lead
              </Button>
            </div>
          );
        })()}
      </div>

      <div className="p-5 space-y-5">
        <AtAGlance lead={lead} items={items} waiting={pendingEmails.length + openTasks.length} />
        <WaitingOnYou workspaceId={workspaceId} lead={lead} pendingEmails={pendingEmails} openTasks={openTasks} onDone={refresh} />

        {/* quick actions */}
        <div className="flex flex-wrap gap-2" data-testid="lead-quick-actions">
          <Button size="sm" variant="outline" onClick={() => setBookOpen(true)} data-testid="button-lead-book"><CalendarPlus /> Book</Button>
          {lead.phone ? (
            <Button size="sm" variant="outline" asChild data-testid="button-lead-call"><a href={telHref(lead.phone)}><PhoneCall /> Call</a></Button>
          ) : (
            <Button size="sm" variant="outline" disabled data-testid="button-lead-call"><PhoneCall /> Call</Button>
          )}
          <Button size="sm" variant={composer === "ask" ? "secondary" : "outline"} onClick={() => { setComposer(composer === "ask" ? null : "ask"); setComposerText(""); }} disabled={!lead.email || lead.optedOut} data-testid="button-lead-email">
            <Mail /> Email
          </Button>
          {lead.stage !== "client" && <Button size="sm" variant="outline" onClick={() => stage.mutate("client")} disabled={busy} data-testid="button-lead-mark-client"><UserCheck /> Mark as client</Button>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="button-lead-more"><MoreHorizontal /> More</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {lead.stage !== "lost" && <DropdownMenuItem onSelect={() => stage.mutate("lost")} data-testid="menu-lead-mark-lost"><UserX size={14} className="mr-2" />Mark as lost</DropdownMenuItem>}
              {!terminal && (
                <DropdownMenuItem onSelect={() => pause.mutate(!paused)} data-testid="menu-lead-pause">
                  {paused ? <><Play size={14} className="mr-2" />Resume follow-ups</> : <><Pause size={14} className="mr-2" />Pause follow-ups</>}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => { setComposer("reply"); setComposerText(""); }} data-testid="menu-lead-add-reply"><Reply size={14} className="mr-2" />Add a reply by hand</DropdownMenuItem>
              <DropdownMenuSeparator />
              {!(String(lead.status).toUpperCase() === "OPTED_OUT") && <DropdownMenuItem onSelect={() => setConfirm("block")} className="text-red-400" data-testid="menu-lead-block"><Ban size={14} className="mr-2" />Block</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onSelect={() => setConfirm("delete")} className="text-red-400" data-testid="menu-lead-delete"><Trash2 size={14} className="mr-2" />Delete</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {composer && (
          <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2" data-testid={`composer-${composer}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{composer === "ask" ? "Ask Rogers to write an email" : `Paste what ${lead.name} replied`}</p>
              <button type="button" onClick={() => setComposer(null)} className="text-muted-foreground hover:text-foreground" data-testid="button-composer-close"><X size={16} /></button>
            </div>
            <p className="text-xs text-muted-foreground">
              {composer === "ask" ? "Say what the email should do. Rogers drafts it; nothing goes out until you approve it." : "Use this when they answered by phone, text or another inbox. Rogers reads it and drafts an answer for your approval."}
            </p>
            <Textarea
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              rows={4}
              placeholder={composer === "ask" ? "e.g. Thank them for the call and send the document checklist" : "Their reply…"}
              data-testid={`textarea-composer-${composer}`}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={!composerText.trim() || ask.isPending || logReply.isPending}
                onClick={() => (composer === "ask" ? ask.mutate(composerText.trim()) : logReply.mutate(composerText.trim()))}
                data-testid={`button-composer-submit-${composer}`}
              >
                {(ask.isPending || logReply.isPending) ? <Loader2 className="animate-spin" /> : <Send />}
                {composer === "ask" ? "Write the draft" : "Add reply"}
              </Button>
            </div>
          </div>
        )}

        <Planned workspaceId={workspaceId} items={items} onDone={refresh} />
        <WhatHappened items={items} lead={lead} />
        <BehindTheScenes items={items} lead={lead} />

        {/* details fold */}
        <div className="rounded-2xl border border-border">
          <button type="button" onClick={() => setDetailsOpen(!detailsOpen)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium" data-testid="button-toggle-lead-details">
            Details
            {detailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {detailsOpen && <Details workspaceId={workspaceId} lead={lead} onSaved={refresh} />}
        </div>
      </div>

      <BookDialog open={bookOpen} onOpenChange={setBookOpen} workspaceId={workspaceId} lead={{ id: lead.id, name: lead.name, email: lead.email, phone: lead.phone }} />

      <AlertDialog open={confirm !== null} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <AlertDialogContent data-testid={`confirm-${confirm ?? "none"}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "delete" ? `Delete ${lead.name}?` : `Block ${lead.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? "This removes the lead and their history from the Lead Desk. It can't be undone."
                : "Rogers stops all follow-ups, unsent emails to them are cancelled, and their address goes on your do-not-email list."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              data-testid="button-confirm-ok"
              onClick={() => { if (confirm === "delete") del.mutate(); else stage.mutate("blocked"); setConfirm(null); }}
            >
              {confirm === "delete" ? "Delete" : "Block"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const isoOrNull = (d: unknown): string | null => (d ? new Date(d as any).toISOString() : null);
const URGENT = new Set(["hot_lead", "callback", "hand_off"]);

// ── Waiting on you: everything that needs the owner, all of it (nothing here has gone out) ─────────────────────────────
function WaitingOnYou({ workspaceId, lead, pendingEmails, openTasks, onDone }: {
  workspaceId: number; lead: TimelineResponse["lead"]; pendingEmails: PendingEmail[]; openTasks: OpenTask[]; onDone: () => void;
}) {
  const urgent = openTasks.filter((t) => URGENT.has(t.kind));
  const other = openTasks.filter((t) => !URGENT.has(t.kind));
  const queue: Array<{ type: "task"; task: OpenTask } | { type: "email"; email: PendingEmail }> = [
    ...urgent.map((task) => ({ type: "task" as const, task })),
    ...pendingEmails.map((email) => ({ type: "email" as const, email })),
    ...other.map((task) => ({ type: "task" as const, task })),
  ];
  if (!queue.length) {
    return (
      <div className="rounded-2xl border border-border bg-card/50 px-4 py-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="next-step-none">
        <Check size={16} className="text-emerald-400" /> Nothing is waiting on you for this lead.
      </div>
    );
  }
  return (
    <section className="space-y-2" data-testid="next-step">
      <SectionTitle title={`Waiting on you (${queue.length})`} hint="None of this has gone out. Rogers needs your OK or your call." tone="amber" />
      {queue.map((q) => q.type === "task"
        ? <TaskCard key={`t${q.task.id}`} workspaceId={workspaceId} task={q.task} phone={lead.phone} onDone={onDone} />
        : <EmailCard key={`e${q.email.id}`} workspaceId={workspaceId} email={q.email} onDone={onDone} />)}
    </section>
  );
}

function SectionTitle({ title, hint, tone }: { title: string; hint?: string; tone?: "amber" }) {
  return (
    <div>
      <div className={cn("text-[11px] uppercase tracking-wider font-semibold", tone === "amber" ? "text-amber-400" : "text-muted-foreground")}>{title}</div>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function TaskCard({ workspaceId, task, phone, onDone }: { workspaceId: number; task: OpenTask; phone: string | null; onDone: () => void }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const close = useMutation({
    mutationFn: (status: "done" | "dismissed") => sendJson("POST", `/api/workspaces/${workspaceId}/lead-tasks/${task.id}/close`, { status }),
    onSuccess: (_r, status) => { onDone(); toast({ title: status === "done" ? "Marked done" : "Dismissed" }); },
    onError: (e: any) => toast({ title: "Couldn't update the task", description: e?.message, variant: "destructive" }),
  });
  const p = task.payload || {};
  const opener: string | null = p.opener || p.callOpener || p.script || p.openingLine || null;
  const hot = task.kind === "hot_lead";
  const callPhone: string | null = p.phone || phone;
  return (
    <div className={cn("rounded-2xl border p-4 space-y-3", hot ? "border-orange-500/40 bg-orange-500/[0.06]" : "border-amber-500/30 bg-amber-500/[0.05]")} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start gap-2">
        {hot ? <Flame size={18} className="text-orange-400 shrink-0 mt-0.5" /> : <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{TASK_KIND_LABEL[task.kind] || task.kind}{task.dueAt ? ` · due ${relTime(task.dueAt)}` : ""}</div>
          <div className="font-medium text-foreground">{task.title}</div>
          {task.detail && <p className="text-sm text-foreground/80 whitespace-pre-wrap mt-1">{task.detail}</p>}
        </div>
      </div>
      {opener && (
        <div className="rounded-xl border border-border bg-background/50 p-3 space-y-1" data-testid="call-opener">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Call opener</span>
            <button type="button" className="text-xs text-blue-400 inline-flex items-center gap-1" onClick={() => { navigator.clipboard?.writeText(opener); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="button-copy-opener">
              {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-sm text-foreground/90 whitespace-pre-wrap">{opener}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {callPhone && (task.kind === "callback" || hot || task.kind === "hand_off") && (
          <Button size="sm" asChild data-testid="button-task-call"><a href={telHref(callPhone)}><PhoneCall /> Call {callPhone}</a></Button>
        )}
        <Button size="sm" variant="outline" onClick={() => close.mutate("done")} disabled={close.isPending} data-testid="button-task-done"><Check /> Done</Button>
        <Button size="sm" variant="ghost" onClick={() => close.mutate("dismissed")} disabled={close.isPending} data-testid="button-task-dismiss">Dismiss</Button>
      </div>
    </div>
  );
}

function EmailCard({ workspaceId, email, onDone }: { workspaceId: number; email: PendingEmail; onDone: () => void }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(email.subject);
  const [body, setBody] = useState(email.rawBody ?? email.body ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setSubject(email.subject); setBody(email.rawBody ?? email.body ?? ""); setProblem(null); setEditing(false); }, [email.id]);

  const approve = useMutation({
    mutationFn: () => sendJson("POST", `/api/workspaces/${workspaceId}/email-outbox/${email.id}/approve`, {}),
    onSuccess: (row: any) => {
      onDone();
      if (row?.status === "FAILED") { setProblem(`It didn't go out: ${row.error || "the send failed"}.`); toast({ title: "The email didn't send", description: row.error || undefined, variant: "destructive" }); }
      else toast({ title: "Sent", description: `"${email.subject}" went to ${email.to}.` });
    },
    onError: (e: any) => { setProblem(e?.message || "Couldn't send"); toast({ title: "Not sent", description: e?.message, variant: "destructive" }); },
  });
  const skip = useMutation({
    mutationFn: () => sendJson("POST", `/api/workspaces/${workspaceId}/email-outbox/${email.id}/discard`, {}),
    onSuccess: () => { onDone(); toast({ title: "Skipped", description: "That email won't be sent." }); },
    onError: (e: any) => toast({ title: "Couldn't skip it", description: e?.message, variant: "destructive" }),
  });
  const save = useMutation({
    mutationFn: () => sendJson("PATCH", `/api/workspaces/${workspaceId}/email-outbox/${email.id}`, { subject, body }),
    onSuccess: () => { setEditing(false); onDone(); toast({ title: "Email updated" }); },
    onError: (e: any) => toast({ title: "Couldn't save", description: e?.message, variant: "destructive" }),
  });
  const long = (email.body || "").length > 400;
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-4 space-y-3" data-testid={`email-card-${email.id}`}>
      <div className="flex items-start gap-2">
        <Mail size={18} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold" data-testid="email-card-state">Draft, NOT sent{email.status === "held" ? " (held)" : ""} · goes out only when you approve</div>
          <div className="text-xs text-muted-foreground">{emailWhat(email.kind)} · to {email.to}</div>
          {editing ? (
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="input-email-subject" />
          ) : (
            <div className="font-medium text-foreground break-words" data-testid="email-card-subject">{email.subject}</div>
          )}
        </div>
      </div>
      {editing ? (
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="font-mono text-xs" data-testid="textarea-email-body" />
      ) : (
        <div>
          <p className={cn("text-sm text-foreground/80 whitespace-pre-wrap break-words", !expanded && long && "line-clamp-6")} data-testid="email-card-body">{email.body}</p>
          {long && <button type="button" className="text-xs text-blue-400 mt-1" onClick={() => setExpanded(!expanded)} data-testid="button-email-expand">{expanded ? "Show less" : "Show all"}</button>}
        </div>
      )}
      {problem && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-300" data-testid="email-card-problem">{problem}</div>}
      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !subject.trim() || !body.trim()} data-testid="button-email-save">{save.isPending ? <Loader2 className="animate-spin" /> : <Check />} Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setSubject(email.subject); setBody(email.rawBody ?? email.body ?? ""); }} data-testid="button-email-cancel-edit">Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={() => { setProblem(null); approve.mutate(); }} disabled={approve.isPending || skip.isPending} data-testid="button-email-approve">
              {approve.isPending ? <Loader2 className="animate-spin" /> : <Send />} Approve and send
            </Button>
            {!email.hasHtml && <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={approve.isPending} data-testid="button-email-edit"><Pencil /> Edit</Button>}
            <Button size="sm" variant="ghost" onClick={() => skip.mutate()} disabled={approve.isPending || skip.isPending} data-testid="button-email-skip">Skip</Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── The lead's story, in four places that never mix ─────────────────────────────────────────────────────────────────
// Bader, 2026-10-15: "I can't figure out what AI did, did they actually reply, what is a planned thing and what is real."
// So: Waiting on you (drafts, tasks) / Planned, not sent yet (future follow-ups, booked calls, emails about to go) /
// What has actually happened (their words, what was sent and who let it go, calls) / Rogers' notes (internal, folded).
const firstName = (name: string | null | undefined): string => String(name || "").trim().split(/\s+/)[0] || "They";
const EMAIL_KIND_LABEL: Record<string, string> = {
  nurture_first: "Rogers' first reply to their enquiry",
  nurture_urgent: "Rogers' quick reply",
  nurture_reply: "Rogers' answer to their message",
  nurture_drip: "Follow-up email",
  nurture_agent: "Email you asked Rogers to write",
};
const emailWhat = (kind: string | null | undefined): string => (kind && EMAIL_KIND_LABEL[kind]) || "Email";
const channelWord = (ch: string | null | undefined): string => (ch === "sms" ? " by text" : ch === "email" ? " by email" : ch === "chat" ? " in the website chat" : "");
const sentByLabel = (m: Record<string, any>): string => (m.sentBy === "you" ? "Sent · you approved it" : m.sentBy === "rogers" ? "Sent · Rogers, automatically" : "Sent");
const CANCELLED_MEETING = /CANCEL|NO_SHOW/i;

type Bucket = "waiting" | "planned" | "happened" | "behind";
function bucketOf(i: TimelineItem): Bucket {
  const m = i.meta || {};
  if (i.kind === "email_out") {
    if (m.pendingApproval) return "waiting";
    if (m.status === "scheduled" || m.status === "sending") return "planned";
    if (m.status === "skipped") return "behind";
    return "happened"; // sent or failed
  }
  if (i.kind === "task") return m.status === "open" ? "waiting" : "behind";
  if (i.kind === "appointment" && CANCELLED_MEETING.test(String(m.status || ""))) return "happened";
  if (m.upcoming === true) return "planned"; // a future call, a follow-up step not written yet
  if (["created", "message_in", "message_out", "chat", "appointment", "callback"].includes(i.kind)) return "happened";
  return "behind"; // Rogers' actions and memories, notes
}

const stepHow = (m: Record<string, any>): string => {
  if (m.stopped) return "Stopped: this lead is a client or lost, so it won't go.";
  if (m.paused) return "Paused: it won't go until you resume follow-ups (More menu).";
  if (m.needsEdit) return `Needs editing before it can go${m.needsEditReason ? `: ${m.needsEditReason}` : "."}`;
  if (m.sendsHow === "auto") return "Goes out automatically (after the email safety checks).";
  if (m.sendsHow === "veto") return `Goes out automatically ${m.vetoHours ? `${m.vetoHours}h after Rogers writes it` : "later"}, unless you skip it.`;
  return "Will wait for your approval before it goes.";
};
const plannedWhen = (i: TimelineItem): string => {
  const m = i.meta || {};
  if (i.kind === "appointment") return dateTime(m.startAt || i.at);
  if (i.kind === "email_out") return m.sendAfter && Date.parse(m.sendAfter) > Date.now() ? dateTime(m.sendAfter) : "within a few minutes";
  return `${m.estimated ? "around " : ""}${dateTime(i.at)}`;
};

function AtAGlance({ lead, items, waiting }: { lead: TimelineResponse["lead"]; items: TimelineItem[]; waiting: number }) {
  const name = firstName(lead.name);
  const lastIn = items.find((i) => i.kind === "message_in");
  const lastOut = items.find((i) => (i.kind === "email_out" && i.meta?.status === "sent") || i.kind === "message_out");
  const next = items
    .filter((i) => bucketOf(i) === "planned" && !(i.kind === "follow_up_step" && (i.meta?.paused || i.meta?.stopped)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))[0];
  const snippet = (s: string | null | undefined) => { const t = String(s || "").replace(/\s+/g, " ").trim(); return t.length > 140 ? `${t.slice(0, 140)}…` : t; };
  const by = (m: Record<string, any>) => (m.sentBy === "you" ? "you approved it" : m.sentBy === "rogers" ? "Rogers sent it automatically" : "sent");
  const nextText = !next ? (lead.nurturePaused ? "Follow-ups are paused. Nothing else is planned." : "Nothing else is planned.")
    : next.kind === "appointment" ? `Call booked for ${plannedWhen(next)}.`
    : next.kind === "email_out" ? `"${next.title}" goes out automatically ${plannedWhen(next)}.`
    : `Follow-up "${next.meta?.subject || "email"}" due ${plannedWhen(next)}. ${stepHow(next.meta || {})}`;
  return (
    <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2 text-sm" data-testid="lead-at-a-glance">
      <SectionTitle title="Where things stand" />
      <p className="flex gap-2" data-testid="glance-last-in">
        <Inbox size={15} className="mt-0.5 shrink-0 text-blue-400" />
        <span>{lastIn
          ? <><span className="font-medium">{name} last wrote {relTime(lastIn.at)}:</span> <span className="text-foreground/80">"{snippet(lastIn.body)}"</span></>
          : <span className="text-foreground/80">{name} hasn't replied since getting in touch.</span>}</span>
      </p>
      <p className="flex gap-2" data-testid="glance-last-out">
        <Send size={15} className="mt-0.5 shrink-0 text-emerald-400" />
        <span>{lastOut
          ? <><span className="font-medium">Last sent to {name} {relTime(lastOut.at)}:</span> <span className="text-foreground/80">{lastOut.kind === "email_out" ? `"${lastOut.title}" (${by(lastOut.meta || {})})` : "a message from Rogers"}</span></>
          : <span className="text-foreground/80">Nothing has been sent to {name} yet.</span>}</span>
      </p>
      {waiting > 0 && (
        <p className="flex gap-2" data-testid="glance-waiting">
          <AlertCircle size={15} className="mt-0.5 shrink-0 text-amber-400" />
          <span className="font-medium text-amber-300">{waiting} thing{waiting === 1 ? "" : "s"} waiting on you (below). Nothing goes out until you act.</span>
        </p>
      )}
      <p className="flex gap-2" data-testid="glance-next">
        <Clock size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
        <span><span className="font-medium">Next:</span> <span className="text-foreground/80">{nextText}</span></span>
      </p>
    </div>
  );
}

function Planned({ workspaceId, items, onDone }: { workspaceId: number; items: TimelineItem[]; onDone: () => void }) {
  const list = items.filter((i) => bucketOf(i) === "planned").sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  return (
    <section className="space-y-2" data-testid="lead-planned">
      <SectionTitle title="Planned, not sent yet" hint={list.length ? "None of this has happened yet. Dates are when it is due." : undefined} />
      {list.length ? <ol className="space-y-2">{list.map((i) => <PlannedRow key={i.id} item={i} workspaceId={workspaceId} onDone={onDone} />)}</ol>
        : <p className="text-sm text-muted-foreground" data-testid="lead-planned-none">Nothing is planned for this lead.</p>}
    </section>
  );
}

function PlannedRow({ item, workspaceId, onDone }: { item: TimelineItem; workspaceId: number; onDone: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const m = item.meta || {};
  const stop = useMutation({
    mutationFn: () => sendJson("POST", `/api/workspaces/${workspaceId}/email-outbox/${m.emailId}/discard`, {}),
    onSuccess: () => { onDone(); toast({ title: "Stopped", description: "That email won't be sent." }); },
    onError: (e: any) => toast({ title: "Couldn't stop it", description: e?.message, variant: "destructive" }),
  });
  const inactive = item.kind === "follow_up_step" && (m.paused || m.stopped);
  let title = item.title;
  let how = "";
  if (item.kind === "follow_up_step") {
    title = `Follow-up ${m.channel === "sms" ? "text" : "email"}${m.of > 1 ? ` ${m.position} of ${m.of}` : ""}: ${m.subject || "untitled"}`;
    how = stepHow(m);
  } else if (item.kind === "appointment") {
    title = `Call booked: ${item.title}`;
    how = m.meetingLink ? "Video call (link below)." : m.phone ? `You call them at ${m.phone}.` : "No phone number or meeting link on the booking.";
  } else if (item.kind === "email_out") {
    title = `Email: ${item.title}`;
    how = `${emailWhat(m.kind)}. Already written; goes out automatically unless you stop it.`;
  }
  const body = item.body || "";
  return (
    <li className={cn("rounded-xl border border-dashed px-3 py-2.5", inactive ? "border-border/60 opacity-60" : "border-border")} data-testid={`planned-${item.id}`}>
      <div className="flex items-start gap-2.5">
        {item.kind === "appointment" ? <Calendar size={15} className="mt-0.5 shrink-0 text-violet-400" /> : <Clock size={15} className="mt-0.5 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm text-foreground break-words min-w-0">{title}</div>
            <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{item.kind === "appointment" ? "Booked" : "Not sent"}</span>
          </div>
          <div className="mt-0.5 text-xs text-foreground/80" data-testid={`planned-when-${item.id}`}>{item.kind === "appointment" ? "When" : "Due"}: {plannedWhen(item)}</div>
          <div className="text-xs text-muted-foreground">{how}</div>
          {item.kind === "appointment" && m.meetingLink && <a href={m.meetingLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">Meeting link</a>}
          {body && item.kind !== "appointment" && (
            <>
              <button type="button" className="mt-1 text-xs text-blue-400" onClick={() => setOpen(!open)} data-testid={`button-planned-read-${item.id}`}>{open ? "Hide the text" : "Read what it will say"}</button>
              {open && <p className="mt-1 rounded-lg bg-muted/30 p-2 text-xs text-foreground/80 whitespace-pre-wrap break-words">{body}</p>}
            </>
          )}
          {item.kind === "email_out" && m.emailId && (
            <div className="mt-1.5"><Button size="sm" variant="outline" onClick={() => stop.mutate()} disabled={stop.isPending} data-testid={`button-planned-stop-${item.id}`}>{stop.isPending ? <Loader2 className="animate-spin" /> : <X />} Stop it</Button></div>
          )}
        </div>
      </div>
    </li>
  );
}

function WhatHappened({ items, lead }: { items: TimelineItem[]; lead: TimelineResponse["lead"] }) {
  const list = items.filter((i) => bucketOf(i) === "happened");
  return (
    <section className="space-y-2" data-testid="lead-timeline">
      <SectionTitle title="What has actually happened" hint={`Newest first. Blue = ${firstName(lead.name)} wrote it. Green = it was sent to them.`} />
      <ol className="space-y-2">{list.map((i) => <HappenedRow key={i.id} item={i} name={firstName(lead.name)} />)}</ol>
    </section>
  );
}

function HappenedRow({ item, name }: { item: TimelineItem; name: string }) {
  const [open, setOpen] = useState(false);
  const m = item.meta || {};
  const fromThem = item.kind === "message_in" || item.kind === "created" || item.kind === "callback";
  const sent = (item.kind === "email_out" && m.status === "sent") || item.kind === "message_out";
  const failed = item.kind === "email_out" && m.status === "failed";
  let chip = "";
  let title = item.title;
  let sub = "";
  let Icon: any = Clock;
  switch (item.kind) {
    case "created": chip = `From ${name}`; title = `${name} got in touch (${m.source || "unknown source"})`; sub = item.body ? "What they wrote:" : ""; Icon = UserPlus; break;
    case "message_in": chip = `From ${name}`; title = `${name} wrote${channelWord(m.channel)}`; Icon = Inbox; break;
    case "callback": chip = `From ${name}`; title = `${name} asked for a call back`; Icon = PhoneCall; break;
    case "email_out":
      chip = failed ? "Not delivered" : sentByLabel(m);
      sub = `${emailWhat(m.kind)}${m.sentBy === "you" && m.approvedBy ? ` · approved by ${m.approvedBy}` : ""}`;
      if (failed && m.error) sub += ` · ${m.error}`;
      Icon = Mail; break;
    case "message_out": chip = "Sent · Rogers"; title = `Rogers replied${channelWord(m.channel)}`; Icon = Bot; break;
    case "chat": chip = "Website chat"; Icon = MessageSquare; break;
    case "appointment": chip = "Call"; title = `Call ${String(m.status || "booked").toLowerCase().replace(/_/g, " ")}: ${dateTime(m.startAt)}`; Icon = Calendar; break;
  }
  const body = item.body || "";
  const long = body.length > 240;
  const transcript: Array<{ role: string; text: string }> | null = item.kind === "chat" && Array.isArray(m.messages) ? m.messages : null;
  return (
    <li className={cn("rounded-xl border px-3 py-2.5", fromThem ? "border-blue-500/30 bg-blue-500/[0.06]" : sent ? "border-emerald-500/25 bg-emerald-500/[0.04]" : failed ? "border-red-500/30 bg-red-500/[0.05]" : "border-border bg-card/40")} data-testid={`timeline-item-${item.id}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={15} className={cn("mt-0.5 shrink-0", fromThem ? "text-blue-400" : sent ? "text-emerald-400" : failed ? "text-red-400" : "text-muted-foreground")} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide", fromThem ? "border-blue-500/40 text-blue-300" : sent ? "border-emerald-500/40 text-emerald-300" : failed ? "border-red-500/40 text-red-300" : "border-border text-muted-foreground")} data-testid={`chip-${item.id}`}>{chip}</span>
            <span className="text-[11px] text-muted-foreground" title={relTime(item.at)}>{dateTime(item.at)}</span>
          </div>
          <div className="mt-1 text-sm text-foreground break-words">{item.kind === "email_out" ? <><span className="text-muted-foreground">Subject: </span>{title}</> : title}</div>
          {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
          {item.kind === "appointment" && m.meetingLink && <a href={m.meetingLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">Meeting link</a>}
          {transcript ? (
            <>
              {body && !open && <p className="mt-1 text-xs text-foreground/70 line-clamp-2">"{body}"</p>}
              <button type="button" className="mt-1 text-xs text-blue-400" onClick={() => setOpen(!open)} data-testid={`button-transcript-${item.id}`}>{open ? "Hide the chat" : "Read the chat"}</button>
              {open && (
                <div className="mt-2 space-y-1.5 max-h-80 overflow-y-auto">
                  {transcript.map((x, i) => (
                    <div key={i} className={cn("flex", x.role === "user" ? "justify-start" : "justify-end")}>
                      <div className={cn("max-w-[85%] rounded-xl px-3 py-1.5 text-xs whitespace-pre-wrap", x.role === "user" ? "bg-blue-500/10" : "bg-emerald-500/10 text-foreground/80")}>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{x.role === "user" ? name : "Rogers"}</div>{x.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : body && item.kind !== "appointment" ? (
            <>
              <p className={cn("mt-1 text-sm text-foreground/85 whitespace-pre-wrap break-words", !open && long && "line-clamp-4")}>{fromThem ? `"${body}"` : body}</p>
              {long && <button type="button" className="text-xs text-blue-400" onClick={() => setOpen(!open)} data-testid={`button-expand-${item.id}`}>{open ? "Less" : "Read all"}</button>}
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}

const BEHIND_ICON: Record<string, any> = { memory: Brain, note: StickyNote, rogers: Sparkles, task: AlertCircle, email_out: Mail };
function BehindTheScenes({ items, lead }: { items: TimelineItem[]; lead: TimelineResponse["lead"] }) {
  const [open, setOpen] = useState(false);
  const list = items.filter((i) => bucketOf(i) === "behind");
  if (!list.length) return null;
  return (
    <div className="rounded-2xl border border-border" data-testid="lead-behind">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-left" data-testid="button-toggle-behind">
        <span>Rogers' notes and actions ({list.length})<span className="block text-xs font-normal text-muted-foreground">Internal only. None of this was sent to {firstName(lead.name)}.</span></span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <ol className="border-t border-border px-4 py-3 space-y-2">
          {list.map((i) => {
            const Icon = BEHIND_ICON[i.kind] || Sparkles;
            const label = i.kind === "email_out" ? `Skipped, not sent: ${i.title}` : i.kind === "task" ? `${i.title} (${String(i.meta?.status || "")})` : i.title;
            return (
              <li key={i.id} className="flex items-start gap-2.5 text-xs" data-testid={`behind-${i.id}`}>
                <Icon size={13} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2"><span className="text-foreground/85 break-words">{label}</span><span className="shrink-0 text-muted-foreground" title={dateTime(i.at)}>{relTime(i.at)}</span></div>
                  {i.body && <p className="mt-0.5 text-muted-foreground whitespace-pre-wrap break-words line-clamp-3">{i.body}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ── Details fold ────────────────────────────────────────────────────────────
const CONSENT_LABEL: Record<string, string> = {
  express: "Said yes (express consent)", implied_inquiry: "Enquiry (implied, 6 months)", implied_client: "Client (implied, 2 years)", none: "No consent",
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-2 text-sm py-1">
      <dt className="text-muted-foreground text-xs pt-0.5">{label}</dt>
      <dd className="text-foreground/90 break-words min-w-0">{children ?? "—"}</dd>
    </div>
  );
}

function Details({ workspaceId, lead, onSaved }: { workspaceId: number; lead: TimelineResponse["lead"]; onSaved: () => void }) {
  const { toast } = useToast();
  const cf = (lead.customFields || {}) as Record<string, string>;
  const [company, setCompany] = useState(cf._prospect_company || "");
  const [industry, setIndustry] = useState(cf._prospect_industry || "");
  const [pain, setPain] = useState(cf._prospect_pain_points || "");
  const dirty = company !== (cf._prospect_company || "") || industry !== (cf._prospect_industry || "") || pain !== (cf._prospect_pain_points || "");
  const save = useMutation({
    mutationFn: () => sendJson("PUT", `/api/leads/${lead.id}`, { customFields: { ...cf, _prospect_company: company.trim(), _prospect_industry: industry.trim(), _prospect_pain_points: pain.trim() } }),
    onSuccess: () => { onSaved(); toast({ title: "Saved", description: "Rogers will use this to personalize messages." }); },
    onError: (e: any) => toast({ title: "Couldn't save", description: e?.message, variant: "destructive" }),
  });
  const c = lead.consent;
  const q = (lead.qualification || {}) as Record<string, string>;
  const utm = [lead.utmSource, lead.utmMedium, lead.utmCampaign, lead.utmContent].some(Boolean);
  return (
    <div className="border-t border-border px-4 py-3 space-y-4" data-testid="lead-details">
      <dl>
        <Row label="Email">{lead.email || "—"}</Row>
        <Row label="Phone">{lead.phone || "—"}</Row>
        <Row label="Source">{lead.source || "—"}</Row>
        {utm && <Row label="UTM">{[lead.utmSource && `source: ${lead.utmSource}`, lead.utmMedium && `medium: ${lead.utmMedium}`, lead.utmCampaign && `campaign: ${lead.utmCampaign}`, lead.utmContent && `content: ${lead.utmContent}`].filter(Boolean).join(" · ")}</Row>}
        <Row label="Landing page">{lead.sourcePageSlug ? <a href={`/p/${lead.sourcePageSlug}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">/p/{lead.sourcePageSlug}</a> : "—"}</Row>
        <Row label="Ad campaign">{lead.sourceAdCampaignId ? `#${lead.sourceAdCampaignId}` : "—"}</Row>
        <Row label="Interests">{lead.interests?.length ? lead.interests.join(", ") : "—"}</Row>
        <Row label="Engagement">{lead.engagementScore ?? 0}{lead.emailOpenCount ? ` · ${lead.emailOpenCount} opens` : ""}{lead.emailClickCount ? ` · ${lead.emailClickCount} clicks` : ""}</Row>
        {lead.fitReason && <Row label="Fit reason">{lead.fitReason}</Row>}
        {Object.keys(q).length > 0 && <Row label="Answers">{Object.entries(q).map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {String(v)}</div>)}</Row>}
      </dl>

      <div className="space-y-1" data-testid="lead-consent">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Consent record</div>
        <dl>
          <Row label="Type">{CONSENT_LABEL[c.type] || c.type}{c.legacy ? " (from before consent was recorded)" : ""}</Row>
          <Row label="Since">{c.since ? dateTime(c.since) : c.legacy ? `${dateTime(isoOrNull(lead.createdAt))} (lead created)` : "—"}</Row>
          <Row label="Until">{c.type === "express" ? "Until they unsubscribe" : c.expiresAt ? dateTime(c.expiresAt) : "—"}</Row>
          <Row label="Source">{c.source || "—"}</Row>
          {c.wording && <Row label="Wording">"{c.wording}"</Row>}
          <Row label="Marketing email"><span className={c.valid && !lead.optedOut ? "text-emerald-400" : "text-amber-400"}>{lead.optedOut ? "Not allowed: opted out" : c.valid ? "Allowed" : "Not allowed"}</span> <span className="text-xs text-muted-foreground">({c.detail})</span></Row>
          {lead.suppression && <Row label="Do-not-email">On the list since {dateTime(lead.suppression.since)} ({lead.suppression.reason})</Row>}
        </dl>
      </div>

      <div className="space-y-2" data-testid="lead-personalization">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Improve Rogers' personalization</div>
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" data-testid="input-enrich-company" />
        <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry" data-testid="input-enrich-industry" />
        <Textarea value={pain} onChange={(e) => setPain(e.target.value)} rows={3} placeholder="What they're struggling with" data-testid="textarea-enrich-pain-points" />
        <div className="flex justify-end">
          <Button size="sm" variant="outline" disabled={!dirty || save.isPending} onClick={() => save.mutate()} data-testid="button-save-enrich">{save.isPending ? <Loader2 className="animate-spin" /> : <Check />} Save</Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Lead #{lead.id} · status {lead.status}</p>
    </div>
  );
}
